import { configureRuntime } from '@/lib/config';

/**
 * 클라이언트 환경변수 접근 지점 — <b>웹(Vite) 전용</b>이다.
 *
 * Vite 는 `VITE_` 접두사가 붙은 값만 번들에 넣는다. 바꿔 말하면
 * `VITE_` 가 붙은 값은 전부 브라우저에서 그대로 읽힌다.
 *
 * v1 은 `NEXT_PUBLIC_SUPABASE_ANON_KEY` 를 번들에 실어 보냈고, 그 키로 앱을 우회해
 * DB 를 직접 읽고 쓸 수 있었다. 아래 가드는 같은 실수를 개발 단계에서 잡는다.
 *
 * <b>이 파일은 frontCore 에 넣지 않는다.</b> `import.meta.env` 는 Vite 전용이라 RN(Metro)에서 깨진다.
 * 코어(client·session)는 여기 대신 {@link runtimeConfig} 를 읽고, 이 파일이 시작 시 그 값을 주입한다
 * (맨 아래 `configureRuntime`). 앱은 자기 부트스트랩에서 같은 주입을 한다.
 */

const SECRET_PATTERNS: RegExp[] = [
  /SUPABASE/i,
  /DATABASE/i,
  /SERVICE_ROLE/i,
  /SECRET/i,
  /PASSWORD/i,
  /ANTHROPIC/i,
  /_KEY$/i,
  /TOKEN$/i,
];

function assertNoLeakedCredentials(): void {
  const leaked = Object.keys(import.meta.env).filter(
    (key) => key.startsWith('VITE_') && SECRET_PATTERNS.some((p) => p.test(key)),
  );

  if (leaked.length === 0) return;

  const message =
    `브라우저 번들에 노출되는 환경변수에 비밀값으로 보이는 항목이 있습니다: ${leaked.join(', ')}\n` +
    'VITE_ 접두사가 붙은 값은 전부 공개됩니다. 비밀값은 백엔드에만 두세요.';

  // 개발 중에는 즉시 실패시켜 배포 전에 잡는다.
  if (import.meta.env.DEV) throw new Error(message);
  // 운영에서 앱을 죽이면 이미 유출된 값을 되돌리지도 못하면서 업무만 멈춘다. 크게 남기고 계속 간다.
  console.error('[SECURITY]', message);
}

assertNoLeakedCredentials();

export const env = {
  /** 기본값은 same-origin. 개발은 Vite 프록시, 운영은 리버스 프록시가 백엔드로 넘긴다. */
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '/api',
  isDev: import.meta.env.DEV,
  /**
   * 개발 중 백엔드에 닿지 못할 때(서버 꺼짐) 목 세션으로 화면을 연다.
   *
   * 화면 데이터는 대부분 이미 목 저장소({@code lib/mock/db.ts})를 보므로, 세션 하나만 흉내 내면
   * 백엔드 없이도 화면을 볼 수 있다. **연결 실패에만 발동**하므로 백엔드를 켜면 실제 세션이 그대로 쓰인다.
   * 운영 번들에서는 `DEV` 가 false 라 절대 켜지지 않는다. 개발에서 진짜 "서버 불통" 화면을 보려면
   * `VITE_MOCK_SESSION=false` 로 끈다.
   */
  mockSessionWhenOffline: import.meta.env.DEV && import.meta.env.VITE_MOCK_SESSION !== 'false',
  /**
   * 그룹웨어(front) 주소. 대시보드의 [입장] 이 그 현장 조회를 여는 곳이다 — 현장 조회는 이 앱에 없다.
   * 개발은 front 의 vite(5173).
   */
  loungeUrl: import.meta.env.VITE_LOUNGE_URL ?? (import.meta.env.DEV ? 'http://localhost:5173' : 'https://vision.bomienc.com'),
} as const;

// 웹의 값을 frontCore 런타임 설정에 주입한다. 코어(client·session)는 import.meta.env 대신 이 설정을 읽는다.
// 이 파일은 웹 부트스트랩(main.tsx)이 시작 시 import 하므로, 첫 API 호출 전에 설정이 채워진다.
configureRuntime({
  apiBaseUrl: env.apiBaseUrl,
  isDev: env.isDev,
  mockSessionWhenOffline: env.mockSessionWhenOffline,
});
